import { getChartData, getDashboard } from 'http/dashboard';

import {
  cancelReasonDefaultOptions,
  defaultDashboardEntity,
  reasonDefaultOptions,
} from 'common/constants';
import { IServerError } from 'common/types';
import {
  IChartData,
  IDashboard,
  TMetric,
  TInterval,
  ISectionItem,
} from 'common/types/dasboard';
import { cents2dollars, notify } from 'common/utils';
import { makeAutoObservable } from 'mobx';

export class DashboardViewModel {
  _isLoading = false;
  today = new Date();
  _startDate = new Date(
    this.today.getFullYear(),
    this.today.getMonth(),
    1,
    0,
    0,
    0,
  );
  _endDate = new Date(
    this.today.getFullYear(),
    this.today.getMonth(),
    this.today.getDate(),
    23,
    59,
    59,
  );
  _metric: TMetric = 'MARGIN';
  _interval: TInterval = 'DAY';
  _dashboard: IDashboard = defaultDashboardEntity;
  _chartData: IChartData = {
    dataPoints: [],
  };

  get isLoading() {
    return this._isLoading;
  }

  set isLoading(isLoading: boolean) {
    this._isLoading = isLoading;
  }

  get startDate() {
    return this._startDate;
  }

  set startDate(startDate: Date) {
    this._startDate = startDate;
  }

  get endDate() {
    return this._endDate;
  }

  set endDate(endDate: Date) {
    this._endDate = endDate;
  }

  get dashboard() {
    return this._dashboard;
  }

  set dashboard(dashboard: IDashboard) {
    this._dashboard = dashboard;
  }

  get dataPoints() {
    return this._chartData.dataPoints;
  }

  set chartData(chartData: IChartData) {
    this._chartData = chartData;
  }

  get metric() {
    return this._metric;
  }

  set metric(metric: TMetric) {
    this._metric = metric;

    this.getChartsData(this.startDate, this.endDate, this.interval, metric);
  }

  get interval() {
    return this._interval;
  }

  set interval(interval: TInterval) {
    this._interval = interval;

    this.getChartsData(this.startDate, this.endDate, interval, this.metric);
  }

  get finance() {
    return [
      {
        label: 'Revenue',
        value: cents2dollars(this.dashboard.totalPaid),
      },
      {
        label: 'Total cost of goods',
        value: cents2dollars(this.dashboard.totalCostOfPurchase),
      },
      {
        label: 'Total cost of shipments',
        value: cents2dollars(this.dashboard.totalCostOfShipping),
      },
      {
        label: 'Margin',
        value: cents2dollars(this.dashboard.totalMargin),
      },
      {
        label: 'Fees',
        value: cents2dollars(this.dashboard.totalFees),
      },
    ];
  }

  get orders() {
    return [
      {
        label: 'Total orders',
        value: this.dashboard.amountOfOrders,
      },
      {
        label: 'Total shipments',
        value: this.dashboard.amountOfShipments,
      },
      {
        label: 'Unique buyers',
        value: this.dashboard.amountOfCustomers,
      },
    ];
  }

  get cancellations() {
    const totalCancellation = Object.values(
      this.dashboard.cancellations.byReason,
    ).reduce<number>((acc, it) => acc + (it ?? 0), 0);

    return [
      {
        label: 'Total amount',
        value: `$${cents2dollars(this.dashboard.cancellations.totalAmount)}`,
      },
      {
        label: 'Total cancellations',
        value: totalCancellation,
      },
      ...cancelReasonDefaultOptions.map((it) => {
        const reason = this.dashboard.cancellations.byReason[it.value];

        return {
          label: it.label,
          value: reason,
        };
      }),
    ];
  }

  get refunds() {
    const totalRefunds = Object.values(
      this.dashboard.refunds.byReason,
    ).reduce<number>((acc, it) => acc + (it ?? 0), 0);

    return [
      {
        label: 'Total amount',
        value: `$${cents2dollars(this.dashboard.refunds.totalAmount)}`,
      },
      {
        label: 'Total refunds',
        value: totalRefunds,
      },
      ...reasonDefaultOptions.map((it) => {
        const reason = this.dashboard.refunds.byReason[it.value];

        return {
          label: it.label,
          value: reason,
        };
      }),
    ];
  }

  onRangeChange = (range: [Date, Date]) => {
    const startDate = new Date(range[0]);
    const endDate = new Date(range[1]);

    startDate.setHours(0, 0, 0);
    endDate.setHours(23, 59, 59);

    this.startDate = startDate;
    this.endDate = endDate;
  };

  onPickerOpenChange = async (isOpen: boolean) => {
    if (isOpen) {
      return;
    }

    await this.getDashboardData(this.startDate, this.endDate);
  };

  getDashboardData = async (startDate: Date, endDate: Date) => {
    try {
      this.isLoading = true;

      const { data: dashboard } = await getDashboard(
        startDate.toDateString(),
        endDate.toDateString(),
      );

      this.getChartsData(startDate, endDate, this.interval, this.metric);

      this.dashboard = dashboard;
    } catch (e) {
      const error = e as IServerError;

      notify(`Error, reason:${error.response?.data?.message}`, 'error');
    } finally {
      this.isLoading = false;
    }
  };

  getChartsData = async (
    startDate: Date,
    endDate: Date,
    interval: TInterval,
    metric: TMetric,
  ) => {
    const { data: chartData } = await getChartData({
      startDate: startDate.toDateString(),
      endDate: endDate.toDateString(),
      interval,
      metric,
    });

    this.chartData = chartData;
  };

  constructor() {
    this.getDashboardData(this.startDate, this.endDate);

    makeAutoObservable(this);
  }
}
